// Pure, owner-neutral search index for the Scripture Journal.
//
// The caller owns account/auth scoping and passes only the JournalEntry rows
// that belong to the active corpus. This leaf deliberately has no store, IDB,
// React, locale, or network dependency. Rebuild it whenever that supplied
// corpus changes. Searchable fields are limited to content the Journal may
// visibly present: frozen references/Scripture, authored bookmark notes and
// prayer text, plus optional caller-supplied trusted BSB text for highlights.
// Parsed prayer metadata, garden summaries, people, testimonies, highlight
// taxonomy and every other hidden/derived field are intentionally absent.

import { parseReference } from './bsb-parser';
import { CANONICAL_BOOKS } from './bsb-types';
import { commonPrefixLength, osaDistanceWithin } from './edit-distance';
import { normalizeForSearch } from './search-normalize';
import type { JournalEntry } from './journal-aggregate';

const SEARCH_TOKEN_RE = /[\p{L}\p{N}]+/gu;
const CANONICAL_SLUGS: ReadonlySet<string> = new Set(
  CANONICAL_BOOKS.map((book) => book.slug),
);

export const JOURNAL_SEARCH_LIMITS = Object.freeze({
  maxEntries: 50_000,
  maxTrustedVerseTexts: 50_000,
  maxVisibleLabels: 50_000,
  maxVisibleLabelLength: 256,
  maxSourceIdLength: 256,
  maxIndexedCharacters: 32 * 1024 * 1024,
  maxIndexedCharactersPerEntry: 8_192,
  maxTokenLength: 64,
  maxUniqueTokens: 100_000,
  maxPostings: 1_000_000,
  maxReferencePostings: 1_000_000,
  maxVersesPerEntry: 256,
  maxNgramLinks: 2_000_000,
  maxQueryLength: 256,
  maxQueryTokens: 12,
  maxResults: 500,
  defaultResults: 100,
  maxLiteralTokenVisits: 1_200_000,
  maxLiteralPostingVisits: 1_500_000,
  maxFuzzyTokenVisits: 20_000,
  maxFuzzyEditCalls: 2_048,
  maxFuzzyPostingVisits: 500_000,
  maxReferenceRange: 256,
  maxFuzzyTokenLength: 48,
} as const);

export type JournalSearchSourceKind = JournalEntry['kind'];

export type JournalSearchField =
  | 'reference'
  | 'authored-note'
  | 'authored-prayer'
  | 'frozen-scripture'
  | 'bsb-scripture'
  | 'visible-label';

export type JournalSearchMatchKind =
  | 'reference'
  | 'exact'
  | 'prefix'
  | 'substring'
  | 'typo';

export interface TrustedJournalVerseText {
  bookSlug: string;
  chapter: number;
  verse: number;
  text: string;
}

/**
 * Optional localized highlight metadata that is actually rendered beside its
 * Journal row (translated family + sublabel). Highlight-only typing keeps this
 * escape hatch away from parsed/Grok prayer fields and other hidden metadata.
 */
export interface VisibleJournalSearchLabel {
  /** Deliberately highlight-only: notes/prayers have no derived labels here. */
  kind: 'highlight';
  sourceId: string;
  text: string;
}

export interface JournalSearchBuildOptions {
  /** Optional, already-trusted BSB verse text. Used only by highlight rows. */
  trustedVerseTexts?: readonly TrustedJournalVerseText[];
  /** Optional rendered labels, keyed by kind + sourceId (first key wins). */
  visibleLabels?: readonly VisibleJournalSearchLabel[];
  /** May lower, never raise, the hard source-entry cap. */
  maxEntries?: number;
}

export interface JournalSearchBuildMetrics {
  inputEntryCount: number;
  acceptedEntryCount: number;
  sourceTruncatedCount: number;
  invalidEntryCount: number;
  duplicateEntryCount: number;
  inputTrustedVerseCount: number;
  acceptedTrustedVerseCount: number;
  trustedVerseTruncatedCount: number;
  invalidTrustedVerseCount: number;
  duplicateTrustedVerseCount: number;
  inputVisibleLabelCount: number;
  acceptedVisibleLabelCount: number;
  visibleLabelTruncatedCount: number;
  invalidVisibleLabelCount: number;
  duplicateVisibleLabelCount: number;
  perEntryCharacterBudget: number;
  indexedFieldCount: number;
  indexedCharacterCount: number;
  truncatedFieldCount: number;
  skippedFieldCharacterCount: number;
  uniqueTokenCount: number;
  skippedTokenOccurrenceCount: number;
  postingCount: number;
  skippedPostingCount: number;
  referencePostingCount: number;
  skippedReferencePostingCount: number;
  truncatedReferenceEntryCount: number;
  ngramKeyCount: number;
  ngramLinkCount: number;
  skippedNgramLinkCount: number;
  /** A transparent sizing model, not a browser heap measurement. */
  estimatedRetainedBytes: number;
  literalIndexComplete: boolean;
  fuzzyIndexComplete: boolean;
}

export interface JournalSearchQueryMetrics {
  inputLength: number;
  boundedInputLength: number;
  queryTokenCount: number;
  skippedOverlongQueryTokenCount: number;
  literalTokenVisits: number;
  literalPostingVisits: number;
  candidateIntersections: number;
  referencePostingVisits: number;
  fuzzyTokenVisits: number;
  fuzzyEditCalls: number;
  fuzzyPostingVisits: number;
  candidateCount: number;
  returnedCount: number;
  queryTruncated: boolean;
  referenceRangeTruncated: boolean;
  workTruncated: boolean;
}

export interface JournalSearchTermMatch {
  queryToken: string;
  quality: JournalSearchMatchKind;
  fields: readonly JournalSearchField[];
  /** Present only for typo terms. */
  editDistance?: number;
}

export interface JournalSearchResult {
  entry: JournalEntry;
  sourceId: string;
  kind: JournalSearchSourceKind;
  matchKind: JournalSearchMatchKind;
  matchedFields: readonly JournalSearchField[];
  termMatches: readonly JournalSearchTermMatch[];
}

export interface JournalSearchResponse {
  results: readonly JournalSearchResult[];
  normalizedQuery: string;
  queryTokens: readonly string[];
  fuzzyFallback: boolean;
  /** Exact only when matchedCountIsExact is true; otherwise a lower bound. */
  matchedCount: number;
  matchedCountIsExact: boolean;
  resultsTruncated: boolean;
  corpusTruncated: boolean;
  metrics: JournalSearchQueryMetrics;
}

interface RawField {
  kind: JournalSearchField;
  text: string;
}

interface RawIndexedField {
  kind: JournalSearchField;
  tokens: readonly string[];
}

interface RawDocument {
  entry: JournalEntry;
  sourceId: string;
  stableKey: string;
  kind: JournalSearchSourceKind;
  updatedMs: number;
  fields: readonly RawIndexedField[];
}

interface IndexedField {
  kind: JournalSearchField;
  tokenIds: Uint32Array;
}

interface IndexedDocument {
  entry: JournalEntry;
  sourceId: string;
  stableKey: string;
  kind: JournalSearchSourceKind;
  updatedMs: number;
  fields: readonly IndexedField[];
  tokenIds: Uint32Array;
}

export interface JournalSearchIndex {
  readonly size: number;
  readonly buildMetrics: Readonly<JournalSearchBuildMetrics>;
  /** Implementation data is exposed read-only so the leaf stays allocation-light. */
  readonly documents: readonly IndexedDocument[];
  readonly tokenList: readonly string[];
  readonly tokenIdByToken: ReadonlyMap<string, number>;
  readonly postings: readonly Uint32Array[];
  readonly characterTokenIds: ReadonlyMap<string, Uint32Array>;
  readonly bigramTokenIds: ReadonlyMap<string, Uint32Array>;
  readonly trigramTokenIds: ReadonlyMap<string, Uint32Array>;
  readonly chapterEntryIds: ReadonlyMap<string, Uint32Array>;
  readonly verseEntryIds: ReadonlyMap<string, Uint32Array>;
}

type MutableBuildMetrics = JournalSearchBuildMetrics;

const FIELD_ORDER: readonly JournalSearchField[] = [
  'reference',
  'authored-note',
  'authored-prayer',
  'frozen-scripture',
  'bsb-scripture',
  'visible-label',
];

function clampInteger(value: number | undefined, fallback: number, max: number): number {
  if (value === undefined || !Number.isFinite(value)) return fallback;
  return Math.max(0, Math.min(max, Math.floor(value)));
}

function sourceIdOf(entry: JournalEntry): string {
  if (entry.kind === 'note') return entry.bookmark.id;
  if (entry.kind === 'highlight') return entry.highlight.id;
  return entry.prayer.id;
}

function createdAtOf(entry: JournalEntry): string {
  if (entry.kind === 'note') return entry.bookmark.createdAt;
  if (entry.kind === 'highlight') return entry.highlight.createdAt;
  return entry.prayer.createdAt;
}

function safeTimestamp(primary: string, fallback: string): number {
  const first = Date.parse(primary);
  if (Number.isFinite(first)) return first;
  const second = Date.parse(fallback);
  return Number.isFinite(second) ? second : 0;
}

function verseKey(bookSlug: string, chapter: number, verse: number): string {
  return `${bookSlug}:${chapter}:${verse}`;
}

function chapterKey(bookSlug: string, chapter: number): string {
  return `${bookSlug}:${chapter}`;
}

function tokenizeNormalized(normalized: string): string[] {
  return normalized.match(SEARCH_TOKEN_RE) ?? [];
}

function uniqueNgrams(value: string, size: number): string[] {
  const characters = Array.from(value);
  if (characters.length < size) return [];
  const grams = new Set<string>();
  for (let index = 0; index <= characters.length - size; index += 1) {
    grams.add(characters.slice(index, index + size).join(''));
  }
  return [...grams];
}

function rawFieldsFor(
  entry: JournalEntry,
  trustedVerseText: ReadonlyMap<string, string>,
  visibleLabels: ReadonlyMap<string, string>,
): RawField[] {
  const visibleLabel = visibleLabels.get(`${entry.kind}:${sourceIdOf(entry)}`) ?? '';
  if (entry.kind === 'note') {
    const anchor = entry.bookmark.anchor;
    return [
      { kind: 'reference', text: `${anchor.reference} ${anchor.bookName ?? ''}` },
      { kind: 'authored-note', text: entry.bookmark.note },
      { kind: 'frozen-scripture', text: anchor.text },
      { kind: 'visible-label', text: visibleLabel },
    ];
  }
  if (entry.kind === 'prayer') {
    const anchor = entry.prayer.scriptureAnchor;
    if (!anchor) return [];
    return [
      { kind: 'reference', text: `${anchor.reference} ${anchor.bookName ?? ''}` },
      { kind: 'authored-prayer', text: entry.prayer.rawText },
      { kind: 'frozen-scripture', text: anchor.text },
      { kind: 'visible-label', text: visibleLabel },
    ];
  }
  return [
    { kind: 'reference', text: entry.highlight.reference },
    {
      kind: 'bsb-scripture',
      text:
        trustedVerseText.get(
          verseKey(
            entry.highlight.bookSlug,
            entry.highlight.chapter,
            entry.highlight.verse,
          ),
        ) ?? '',
    },
    { kind: 'visible-label', text: visibleLabel },
  ];
}

function locationOf(entry: JournalEntry): {
  bookSlug: string;
  chapter: number;
  verses: readonly number[];
} | null {
  if (entry.kind === 'highlight') {
    return {
      bookSlug: entry.highlight.bookSlug,
      chapter: entry.highlight.chapter,
      verses: [entry.highlight.verse],
    };
  }
  const anchor =
    entry.kind === 'note' ? entry.bookmark.anchor : entry.prayer.scriptureAnchor;
  if (!anchor) return null;
  return {
    bookSlug: anchor.bookSlug,
    chapter: anchor.chapter,
    verses: anchor.verses,
  };
}

function pushMapArray(
  map: Map<string, number[]>,
  key: string,
  value: number,
): void {
  const current = map.get(key);
  if (current) current.push(value);
  else map.set(key, [value]);
}

function freezeNumberMap(map: Map<string, number[]>): Map<string, Uint32Array> {
  return new Map(
    [...map].map(([key, values]) => [key, Uint32Array.from(values)] as const),
  );
}

function buildTrustedVerseTextMap(
  rows: readonly TrustedJournalVerseText[],
  metrics: MutableBuildMetrics,
): Map<string, string> {
  const result = new Map<string, string>();
  const accepted = rows.slice(0, JOURNAL_SEARCH_LIMITS.maxTrustedVerseTexts);
  metrics.trustedVerseTruncatedCount = rows.length - accepted.length;
  if (metrics.trustedVerseTruncatedCount > 0) metrics.literalIndexComplete = false;
  for (const row of accepted) {
    if (
      !CANONICAL_SLUGS.has(row.bookSlug) ||
      !Number.isInteger(row.chapter) ||
      row.chapter <= 0 ||
      !Number.isInteger(row.verse) ||
      row.verse <= 0 ||
      typeof row.text !== 'string' ||
      row.text.length === 0
    ) {
      metrics.invalidTrustedVerseCount += 1;
      continue;
    }
    const key = verseKey(row.bookSlug, row.chapter, row.verse);
    if (result.has(key)) {
      metrics.duplicateTrustedVerseCount += 1;
      continue;
    }
    result.set(key, row.text);
  }
  metrics.acceptedTrustedVerseCount = result.size;
  return result;
}

function buildVisibleLabelMap(
  rows: readonly VisibleJournalSearchLabel[],
  metrics: MutableBuildMetrics,
): Map<string, string> {
  const result = new Map<string, string>();
  const accepted = rows.slice(0, JOURNAL_SEARCH_LIMITS.maxVisibleLabels);
  metrics.visibleLabelTruncatedCount = rows.length - accepted.length;
  if (metrics.visibleLabelTruncatedCount > 0) metrics.literalIndexComplete = false;
  for (const row of accepted) {
    if (
      row.kind !== 'highlight' ||
      typeof row.sourceId !== 'string' ||
      row.sourceId.length === 0 ||
      row.sourceId.length > JOURNAL_SEARCH_LIMITS.maxSourceIdLength ||
      typeof row.text !== 'string' ||
      row.text.trim().length === 0
    ) {
      metrics.invalidVisibleLabelCount += 1;
      continue;
    }
    const key = `${row.kind}:${row.sourceId}`;
    if (result.has(key)) {
      metrics.duplicateVisibleLabelCount += 1;
      continue;
    }
    result.set(key, row.text.slice(0, JOURNAL_SEARCH_LIMITS.maxVisibleLabelLength));
  }
  metrics.acceptedVisibleLabelCount = result.size;
  return result;
}

function initialBuildMetrics(
  entryCount: number,
  trustedVerseCount: number,
  visibleLabelCount: number,
): MutableBuildMetrics {
  return {
    inputEntryCount: entryCount,
    acceptedEntryCount: 0,
    sourceTruncatedCount: 0,
    invalidEntryCount: 0,
    duplicateEntryCount: 0,
    inputTrustedVerseCount: trustedVerseCount,
    acceptedTrustedVerseCount: 0,
    trustedVerseTruncatedCount: 0,
    invalidTrustedVerseCount: 0,
    duplicateTrustedVerseCount: 0,
    inputVisibleLabelCount: visibleLabelCount,
    acceptedVisibleLabelCount: 0,
    visibleLabelTruncatedCount: 0,
    invalidVisibleLabelCount: 0,
    duplicateVisibleLabelCount: 0,
    perEntryCharacterBudget: 0,
    indexedFieldCount: 0,
    indexedCharacterCount: 0,
    truncatedFieldCount: 0,
    skippedFieldCharacterCount: 0,
    uniqueTokenCount: 0,
    skippedTokenOccurrenceCount: 0,
    postingCount: 0,
    skippedPostingCount: 0,
    referencePostingCount: 0,
    skippedReferencePostingCount: 0,
    truncatedReferenceEntryCount: 0,
    ngramKeyCount: 0,
    ngramLinkCount: 0,
    skippedNgramLinkCount: 0,
    estimatedRetainedBytes: 0,
    literalIndexComplete: true,
    fuzzyIndexComplete: true,
  };
}

/** Build a bounded snapshot over caller-supplied, already owner-scoped entries. */
export function buildJournalSearchIndex(
  entries: readonly JournalEntry[],
  options: JournalSearchBuildOptions = {},
): JournalSearchIndex {
  const trustedRows = options.trustedVerseTexts ?? [];
  const visibleLabelRows = options.visibleLabels ?? [];
  const metrics = initialBuildMetrics(
    entries.length,
    trustedRows.length,
    visibleLabelRows.length,
  );
  const trustedVerseText = buildTrustedVerseTextMap(trustedRows, metrics);
  const visibleLabels = buildVisibleLabelMap(visibleLabelRows, metrics);
  const maxEntries = clampInteger(
    options.maxEntries,
    JOURNAL_SEARCH_LIMITS.maxEntries,
    JOURNAL_SEARCH_LIMITS.maxEntries,
  );
  const boundedEntries = entries.slice(0, maxEntries);
  metrics.sourceTruncatedCount = entries.length - boundedEntries.length;

  const acceptedEntries: JournalEntry[] = [];
  const seen = new Set<string>();
  for (const entry of boundedEntries) {
    const sourceId = sourceIdOf(entry);
    if (
      typeof sourceId !== 'string' ||
      sourceId.length === 0 ||
      sourceId.length > JOURNAL_SEARCH_LIMITS.maxSourceIdLength
    ) {
      metrics.invalidEntryCount += 1;
      continue;
    }
    const stableKey = `${entry.kind}:${sourceId}`;
    if (seen.has(stableKey)) {
      metrics.duplicateEntryCount += 1;
      continue;
    }
    seen.add(stableKey);
    acceptedEntries.push(entry);
  }
  metrics.acceptedEntryCount = acceptedEntries.length;
  metrics.perEntryCharacterBudget =
    acceptedEntries.length === 0
      ? 0
      : Math.min(
          JOURNAL_SEARCH_LIMITS.maxIndexedCharactersPerEntry,
          Math.floor(
            JOURNAL_SEARCH_LIMITS.maxIndexedCharacters / acceptedEntries.length,
          ),
        );

  const tokenUniverse = new Set<string>();
  const rawDocuments: RawDocument[] = [];
  for (const entry of acceptedEntries) {
    const sourceId = sourceIdOf(entry);
    const rawFields = rawFieldsFor(entry, trustedVerseText, visibleLabels).filter(
      (field) => typeof field.text === 'string' && field.text.length > 0,
    );
    let remaining = metrics.perEntryCharacterBudget;
    const indexedFields: RawIndexedField[] = [];
    for (let fieldIndex = 0; fieldIndex < rawFields.length; fieldIndex += 1) {
      const field = rawFields[fieldIndex];
      const fieldsLeft = rawFields.length - fieldIndex;
      const allowance = fieldsLeft > 0 ? Math.floor(remaining / fieldsLeft) : 0;
      const bounded = field.text.slice(0, allowance);
      if (bounded.length < field.text.length) {
        metrics.truncatedFieldCount += 1;
        metrics.skippedFieldCharacterCount += field.text.length - bounded.length;
        metrics.literalIndexComplete = false;
      }
      remaining -= bounded.length;
      const normalized = normalizeForSearch(bounded);
      metrics.indexedCharacterCount += normalized.length;
      const tokens = [
        ...new Set(
          tokenizeNormalized(normalized).filter(
            (token) => token.length <= JOURNAL_SEARCH_LIMITS.maxTokenLength,
          ),
        ),
      ].sort();
      for (const token of tokens) {
        if (
          !tokenUniverse.has(token) &&
          tokenUniverse.size >= JOURNAL_SEARCH_LIMITS.maxUniqueTokens
        ) {
          metrics.skippedTokenOccurrenceCount += 1;
          metrics.literalIndexComplete = false;
          continue;
        }
        tokenUniverse.add(token);
      }
      indexedFields.push({ kind: field.kind, tokens });
      metrics.indexedFieldCount += 1;
    }
    rawDocuments.push({
      entry,
      sourceId,
      stableKey: `${entry.kind}:${sourceId}`,
      kind: entry.kind,
      updatedMs: safeTimestamp(entry.updatedAt, createdAtOf(entry)),
      fields: indexedFields,
    });
  }

  const tokenList = [...tokenUniverse].sort();
  const tokenIdByToken = new Map(
    tokenList.map((token, tokenId) => [token, tokenId] as const),
  );
  metrics.uniqueTokenCount = tokenList.length;
  const mutablePostings: number[][] = Array.from(
    { length: tokenList.length },
    () => [],
  );
  const documents: IndexedDocument[] = [];
  for (let documentIndex = 0; documentIndex < rawDocuments.length; documentIndex += 1) {
    const raw = rawDocuments[documentIndex];
    const fields: IndexedField[] = raw.fields.map((field) => ({
      kind: field.kind,
      tokenIds: Uint32Array.from(
        field.tokens
          .map((token) => tokenIdByToken.get(token))
          .filter((tokenId): tokenId is number => tokenId !== undefined),
      ),
    }));
    const allTokenIds = [
      ...new Set(fields.flatMap((field) => [...field.tokenIds])),
    ].sort((left, right) => left - right);
    for (const tokenId of allTokenIds) {
      if (metrics.postingCount >= JOURNAL_SEARCH_LIMITS.maxPostings) {
        metrics.skippedPostingCount += 1;
        metrics.literalIndexComplete = false;
        continue;
      }
      mutablePostings[tokenId].push(documentIndex);
      metrics.postingCount += 1;
    }
    documents.push({
      entry: raw.entry,
      sourceId: raw.sourceId,
      stableKey: raw.stableKey,
      kind: raw.kind,
      updatedMs: raw.updatedMs,
      fields,
      tokenIds: Uint32Array.from(allTokenIds),
    });
  }
  const postings = mutablePostings.map((values) => Uint32Array.from(values));

  const chapterLists = new Map<string, number[]>();
  const verseLists = new Map<string, number[]>();
  for (let documentIndex = 0; documentIndex < documents.length; documentIndex += 1) {
    const location = locationOf(documents[documentIndex].entry);
    if (
      !location ||
      !CANONICAL_SLUGS.has(location.bookSlug) ||
      !Number.isInteger(location.chapter) ||
      location.chapter <= 0
    ) continue;
    const addReference = (map: Map<string, number[]>, key: string): void => {
      if (
        metrics.referencePostingCount >=
        JOURNAL_SEARCH_LIMITS.maxReferencePostings
      ) {
        metrics.skippedReferencePostingCount += 1;
        metrics.literalIndexComplete = false;
        return;
      }
      pushMapArray(map, key, documentIndex);
      metrics.referencePostingCount += 1;
    };
    addReference(chapterLists, chapterKey(location.bookSlug, location.chapter));
    const verses = [
      ...new Set(
        location.verses.filter(
          (verse) => Number.isInteger(verse) && verse > 0,
        ),
      ),
    ];
    if (verses.length > JOURNAL_SEARCH_LIMITS.maxVersesPerEntry) {
      metrics.truncatedReferenceEntryCount += 1;
      metrics.literalIndexComplete = false;
    }
    for (const verse of verses.slice(0, JOURNAL_SEARCH_LIMITS.maxVersesPerEntry)) {
      addReference(
        verseLists,
        verseKey(location.bookSlug, location.chapter, verse),
      );
    }
  }

  const characterLists = new Map<string, number[]>();
  const bigramLists = new Map<string, number[]>();
  const trigramLists = new Map<string, number[]>();
  let ngramBudgetExhausted = false;
  const addNgram = (map: Map<string, number[]>, key: string, tokenId: number): void => {
    if (metrics.ngramLinkCount >= JOURNAL_SEARCH_LIMITS.maxNgramLinks) {
      metrics.skippedNgramLinkCount += 1;
      ngramBudgetExhausted = true;
      return;
    }
    pushMapArray(map, key, tokenId);
    metrics.ngramLinkCount += 1;
  };
  for (let tokenId = 0; tokenId < tokenList.length; tokenId += 1) {
    const token = tokenList[tokenId];
    for (const character of new Set(Array.from(token))) {
      addNgram(characterLists, character, tokenId);
    }
    for (const gram of uniqueNgrams(token, 2)) addNgram(bigramLists, gram, tokenId);
    for (const gram of uniqueNgrams(token, 3)) addNgram(trigramLists, gram, tokenId);
  }
  metrics.ngramKeyCount =
    characterLists.size + bigramLists.size + trigramLists.size;
  metrics.fuzzyIndexComplete = !ngramBudgetExhausted;

  const fieldTokenIds = documents.reduce(
    (total, document) =>
      total + document.fields.reduce((sum, field) => sum + field.tokenIds.length, 0),
    0,
  );
  const tokenBytes = tokenList.reduce(
    (total, token) => total + token.length * 2,
    0,
  );
  const referenceKeyCount = chapterLists.size + verseLists.size;
  // Transparent engineering estimate: UTF-16 token payload + Uint32 payloads
  // + conservative fixed overhead for documents, fields, and Map entries.
  // Browser/engine object overhead differs; the probe also prints observed V8
  // heap delta when run with --expose-gc.
  metrics.estimatedRetainedBytes =
    tokenBytes +
    fieldTokenIds * 4 +
    metrics.postingCount * 4 +
    metrics.referencePostingCount * 4 +
    metrics.ngramLinkCount * 4 +
    documents.length * 128 +
    metrics.indexedFieldCount * 48 +
    (tokenList.length + metrics.ngramKeyCount + referenceKeyCount) * 48;

  return {
    size: documents.length,
    buildMetrics: Object.freeze({ ...metrics }),
    documents,
    tokenList,
    tokenIdByToken,
    postings,
    characterTokenIds: freezeNumberMap(characterLists),
    bigramTokenIds: freezeNumberMap(bigramLists),
    trigramTokenIds: freezeNumberMap(trigramLists),
    chapterEntryIds: freezeNumberMap(chapterLists),
    verseEntryIds: freezeNumberMap(verseLists),
  };
}

interface PreparedQuery {
  bounded: string;
  normalized: string;
  tokens: string[];
  truncated: boolean;
  skippedOverlong: number;
}

function prepareQuery(query: string): PreparedQuery {
  const bounded = query.slice(0, JOURNAL_SEARCH_LIMITS.maxQueryLength);
  const normalized = normalizeForSearch(bounded);
  const all = [...new Set(tokenizeNormalized(normalized))];
  const withinLength = all.filter(
    (token) => token.length <= JOURNAL_SEARCH_LIMITS.maxTokenLength,
  );
  return {
    bounded,
    normalized,
    tokens: withinLength.slice(0, JOURNAL_SEARCH_LIMITS.maxQueryTokens),
    truncated:
      bounded.length < query.length ||
      withinLength.length > JOURNAL_SEARCH_LIMITS.maxQueryTokens ||
      withinLength.length < all.length,
    skippedOverlong: all.length - withinLength.length,
  };
}

function initialQueryMetrics(query: string, prepared: PreparedQuery): JournalSearchQueryMetrics {
  return {
    inputLength: query.length,
    boundedInputLength: prepared.bounded.length,
    queryTokenCount: prepared.tokens.length,
    skippedOverlongQueryTokenCount: prepared.skippedOverlong,
    literalTokenVisits: 0,
    literalPostingVisits: 0,
    candidateIntersections: 0,
    referencePostingVisits: 0,
    fuzzyTokenVisits: 0,
    fuzzyEditCalls: 0,
    fuzzyPostingVisits: 0,
    candidateCount: 0,
    returnedCount: 0,
    queryTruncated: prepared.truncated,
    referenceRangeTruncated: false,
    workTruncated: false,
  };
}

function lowerBound(sorted: readonly string[], target: string): number {
  let low = 0;
  let high = sorted.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (sorted[middle] < target) low = middle + 1;
    else high = middle;
  }
  return low;
}

function compareCodeUnits(left: string, right: string): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

const LITERAL_SUBSTRING = 1;
const LITERAL_PREFIX = 2;
const LITERAL_EXACT = 3;

function tokenCandidateQualities(
  word: string,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): Map<number, number> {
  const qualities = new Map<number, number>();
  const visit = (tokenId: number, quality: number): boolean => {
    if (
      metrics.literalTokenVisits >=
      JOURNAL_SEARCH_LIMITS.maxLiteralTokenVisits
    ) {
      metrics.workTruncated = true;
      return false;
    }
    metrics.literalTokenVisits += 1;
    qualities.set(tokenId, Math.max(qualities.get(tokenId) ?? 0, quality));
    return true;
  };

  const exactId = index.tokenIdByToken.get(word);
  if (exactId !== undefined) visit(exactId, LITERAL_EXACT);

  for (
    let tokenId = lowerBound(index.tokenList, word);
    tokenId < index.tokenList.length && index.tokenList[tokenId].startsWith(word);
    tokenId += 1
  ) {
    if (!visit(tokenId, tokenId === exactId ? LITERAL_EXACT : LITERAL_PREFIX)) break;
  }

  let substringCandidates: Iterable<number>;
  if (index.buildMetrics.fuzzyIndexComplete) {
    const chars = Array.from(word);
    if (chars.length >= 3) {
      const lists = uniqueNgrams(word, 3)
        .map((gram) => index.trigramTokenIds.get(gram))
        .filter((list): list is Uint32Array => list !== undefined)
        .sort((left, right) => left.length - right.length);
      if (lists.length !== uniqueNgrams(word, 3).length) return qualities;
      substringCandidates = lists[0] ?? [];
    } else if (chars.length === 2) {
      substringCandidates = index.bigramTokenIds.get(chars.join('')) ?? [];
    } else {
      substringCandidates = index.characterTokenIds.get(chars[0] ?? '') ?? [];
    }
  } else {
    // The retained n-gram accelerator hit its build cap. A full vocabulary scan
    // preserves literal correctness; it is still bounded by maxUniqueTokens ×
    // maxQueryTokens and accounted in literalTokenVisits.
    substringCandidates = index.tokenList.map((_, tokenId) => tokenId);
  }
  for (const tokenId of substringCandidates) {
    const token = index.tokenList[tokenId];
    if (!token.includes(word)) continue;
    const quality =
      token === word
        ? LITERAL_EXACT
        : token.startsWith(word)
          ? LITERAL_PREFIX
          : LITERAL_SUBSTRING;
    if (!visit(tokenId, quality)) break;
  }
  return qualities;
}

function literalMatchesForWord(
  word: string,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): Map<number, number> {
  const result = new Map<number, number>();
  for (const [tokenId, quality] of tokenCandidateQualities(word, index, metrics)) {
    for (const documentIndex of index.postings[tokenId]) {
      if (
        metrics.literalPostingVisits >=
        JOURNAL_SEARCH_LIMITS.maxLiteralPostingVisits
      ) {
        metrics.workTruncated = true;
        return result;
      }
      metrics.literalPostingVisits += 1;
      result.set(
        documentIndex,
        Math.max(result.get(documentIndex) ?? 0, quality),
      );
    }
  }
  return result;
}

interface LiteralCandidate {
  structuralReference: boolean;
  weakestQuality: number;
  qualityTotal: number;
  exactCount: number;
  prefixCount: number;
}

function literalCandidates(
  prepared: PreparedQuery,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): Map<number, LiteralCandidate> {
  let candidates: Map<number, LiteralCandidate> | null = null;
  const orderedWords = [...prepared.tokens].sort((left, right) => {
    const leftSize = index.postings[index.tokenIdByToken.get(left) ?? -1]?.length ?? 0;
    const rightSize = index.postings[index.tokenIdByToken.get(right) ?? -1]?.length ?? 0;
    return leftSize - rightSize || compareCodeUnits(left, right);
  });
  for (const word of orderedWords) {
    const matches = literalMatchesForWord(word, index, metrics);
    if (candidates === null) {
      candidates = new Map(
        [...matches].map(([documentIndex, quality]) => [
          documentIndex,
          {
            structuralReference: false,
            weakestQuality: quality,
            qualityTotal: quality,
            exactCount: quality === LITERAL_EXACT ? 1 : 0,
            prefixCount: quality === LITERAL_PREFIX ? 1 : 0,
          },
        ]),
      );
    } else {
      for (const [documentIndex, candidate] of candidates) {
        const quality = matches.get(documentIndex);
        metrics.candidateIntersections += 1;
        if (quality === undefined) {
          candidates.delete(documentIndex);
          continue;
        }
        candidate.weakestQuality = Math.min(candidate.weakestQuality, quality);
        candidate.qualityTotal += quality;
        if (quality === LITERAL_EXACT) candidate.exactCount += 1;
        if (quality === LITERAL_PREFIX) candidate.prefixCount += 1;
      }
    }
    if (candidates.size === 0) break;
  }
  return candidates ?? new Map();
}

function structuralReferenceMatches(
  prepared: PreparedQuery,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): Set<number> {
  const parsed = parseReference(prepared.bounded);
  if (!parsed) return new Set();
  const result = new Set<number>();
  const visit = (values: Uint32Array | undefined): void => {
    if (!values) return;
    for (const documentIndex of values) {
      if (
        metrics.referencePostingVisits >=
        JOURNAL_SEARCH_LIMITS.maxReferencePostings
      ) {
        metrics.workTruncated = true;
        return;
      }
      metrics.referencePostingVisits += 1;
      result.add(documentIndex);
    }
  };
  if (parsed.verseStart === null) {
    visit(index.chapterEntryIds.get(chapterKey(parsed.bookSlug, parsed.chapter)));
    return result;
  }
  const requestedEnd = parsed.verseEnd ?? parsed.verseStart;
  const boundedEnd = Math.min(
    requestedEnd,
    parsed.verseStart + JOURNAL_SEARCH_LIMITS.maxReferenceRange - 1,
  );
  if (boundedEnd < requestedEnd) {
    metrics.referenceRangeTruncated = true;
    metrics.workTruncated = true;
  }
  for (let verse = parsed.verseStart; verse <= boundedEnd; verse += 1) {
    visit(index.verseEntryIds.get(verseKey(parsed.bookSlug, parsed.chapter, verse)));
  }
  return result;
}

interface FuzzyTokenMatch {
  tokenId: number;
  distance: number;
  prefix: number;
}

function fuzzyThreshold(length: number): number {
  return length >= 7 ? 2 : 1;
}

function fuzzyTokenMatches(
  word: string,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): FuzzyTokenMatch[] {
  if (
    word.length < 4 ||
    word.length > JOURNAL_SEARCH_LIMITS.maxFuzzyTokenLength ||
    metrics.fuzzyEditCalls >= JOURNAL_SEARCH_LIMITS.maxFuzzyEditCalls
  ) return [];
  if (!index.buildMetrics.fuzzyIndexComplete) metrics.workTruncated = true;
  const threshold = fuzzyThreshold(word.length);
  const sources: Array<{ ids: Uint32Array; weight: number }> = [];
  for (const gram of uniqueNgrams(word, 2)) {
    const ids = index.bigramTokenIds.get(gram);
    if (ids) sources.push({ ids, weight: 2 });
  }
  for (const character of new Set(Array.from(word))) {
    const ids = index.characterTokenIds.get(character);
    if (ids) sources.push({ ids, weight: 1 });
  }
  sources.sort((left, right) => left.ids.length - right.ids.length);
  const strength = new Map<number, number>();
  for (const source of sources) {
    for (const tokenId of source.ids) {
      if (
        metrics.fuzzyTokenVisits >=
        JOURNAL_SEARCH_LIMITS.maxFuzzyTokenVisits
      ) {
        metrics.workTruncated = true;
        break;
      }
      metrics.fuzzyTokenVisits += 1;
      const token = index.tokenList[tokenId];
      if (
        token === word ||
        token.length > JOURNAL_SEARCH_LIMITS.maxFuzzyTokenLength ||
        Math.abs(token.length - word.length) > threshold
      ) continue;
      strength.set(tokenId, (strength.get(tokenId) ?? 0) + source.weight);
    }
    if (metrics.workTruncated) break;
  }
  const ranked = [...strength].sort((left, right) => {
    if (right[1] !== left[1]) return right[1] - left[1];
    const leftToken = index.tokenList[left[0]];
    const rightToken = index.tokenList[right[0]];
    const prefixDiff =
      commonPrefixLength(word, rightToken) - commonPrefixLength(word, leftToken);
    if (prefixDiff !== 0) return prefixDiff;
    return compareCodeUnits(leftToken, rightToken);
  });
  const result: FuzzyTokenMatch[] = [];
  for (const [tokenId] of ranked) {
    if (metrics.fuzzyEditCalls >= JOURNAL_SEARCH_LIMITS.maxFuzzyEditCalls) {
      metrics.workTruncated = true;
      break;
    }
    const token = index.tokenList[tokenId];
    metrics.fuzzyEditCalls += 1;
    const distance = osaDistanceWithin(word, token, threshold);
    if (distance <= threshold) {
      result.push({
        tokenId,
        distance,
        prefix: commonPrefixLength(word, token),
      });
    }
  }
  return result;
}

interface FuzzyWordDocumentMatch {
  distance: number;
  prefix: number;
  literal: boolean;
}

function fuzzyWordDocuments(
  word: string,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
  tokenMatches: readonly FuzzyTokenMatch[],
): Map<number, FuzzyWordDocumentMatch> {
  const result = new Map<number, FuzzyWordDocumentMatch>();
  const literal = literalMatchesForWord(word, index, metrics);
  for (const documentIndex of literal.keys()) {
    result.set(documentIndex, {
      distance: 0,
      prefix: word.length,
      literal: true,
    });
  }
  for (const tokenMatch of tokenMatches) {
    for (const documentIndex of index.postings[tokenMatch.tokenId]) {
      if (
        metrics.fuzzyPostingVisits >=
        JOURNAL_SEARCH_LIMITS.maxFuzzyPostingVisits
      ) {
        metrics.workTruncated = true;
        return result;
      }
      metrics.fuzzyPostingVisits += 1;
      const current = result.get(documentIndex);
      if (
        !current ||
        (!current.literal &&
          (tokenMatch.distance < current.distance ||
            (tokenMatch.distance === current.distance &&
              tokenMatch.prefix > current.prefix)))
      ) {
        result.set(documentIndex, {
          distance: tokenMatch.distance,
          prefix: tokenMatch.prefix,
          literal: false,
        });
      }
    }
  }
  return result;
}

interface FuzzyCandidate {
  distance: number;
  prefix: number;
  literalWords: number;
}

function fuzzyCandidates(
  prepared: PreparedQuery,
  index: JournalSearchIndex,
  metrics: JournalSearchQueryMetrics,
): {
  candidates: Map<number, FuzzyCandidate>;
  tokenMatchesByWord: readonly (ReadonlyMap<number, FuzzyTokenMatch>)[];
} {
  let candidates: Map<number, FuzzyCandidate> | null = null;
  const tokenMatchesByWord: Array<ReadonlyMap<number, FuzzyTokenMatch>> = [];
  for (const word of prepared.tokens) {
    const tokenMatches = fuzzyTokenMatches(word, index, metrics);
    tokenMatchesByWord.push(
      new Map(tokenMatches.map((match) => [match.tokenId, match] as const)),
    );
    const documents = fuzzyWordDocuments(word, index, metrics, tokenMatches);
    if (candidates === null) {
      candidates = new Map(
        [...documents].map(([documentIndex, match]) => [
          documentIndex,
          {
            distance: match.distance,
            prefix: match.prefix,
            literalWords: match.literal ? 1 : 0,
          },
        ]),
      );
    } else {
      for (const [documentIndex, candidate] of candidates) {
        const match = documents.get(documentIndex);
        metrics.candidateIntersections += 1;
        if (!match) {
          candidates.delete(documentIndex);
          continue;
        }
        candidate.distance += match.distance;
        candidate.prefix += match.prefix;
        if (match.literal) candidate.literalWords += 1;
      }
    }
    if (candidates.size === 0) break;
  }
  return { candidates: candidates ?? new Map(), tokenMatchesByWord };
}

function compareStableDocuments(left: IndexedDocument, right: IndexedDocument): number {
  if (right.updatedMs !== left.updatedMs) return right.updatedMs - left.updatedMs;
  return compareCodeUnits(left.stableKey, right.stableKey);
}

function literalQuality(token: string, word: string): number {
  if (token === word) return LITERAL_EXACT;
  if (token.startsWith(word)) return LITERAL_PREFIX;
  if (token.includes(word)) return LITERAL_SUBSTRING;
  return 0;
}

function qualityName(quality: number): JournalSearchMatchKind {
  if (quality >= LITERAL_EXACT) return 'exact';
  if (quality === LITERAL_PREFIX) return 'prefix';
  return 'substring';
}

function describeTermMatches(
  document: IndexedDocument,
  prepared: PreparedQuery,
  index: JournalSearchIndex,
  fuzzyTokenMatchesByWord?: readonly (ReadonlyMap<number, FuzzyTokenMatch>)[],
): JournalSearchTermMatch[] {
  return prepared.tokens.map((word, wordIndex) => {
    let bestQuality = 0;
    let bestDistance = Number.POSITIVE_INFINITY;
    let bestPrefix = -1;
    const fields = new Set<JournalSearchField>();
    for (const field of document.fields) {
      let fieldQuality = 0;
      let fieldDistance = Number.POSITIVE_INFINITY;
      let fieldPrefix = -1;
      for (const tokenId of field.tokenIds) {
        const token = index.tokenList[tokenId];
        fieldQuality = Math.max(fieldQuality, literalQuality(token, word));
        const fuzzy = fuzzyTokenMatchesByWord?.[wordIndex]?.get(tokenId);
        if (
          fuzzy &&
          (fuzzy.distance < fieldDistance ||
            (fuzzy.distance === fieldDistance && fuzzy.prefix > fieldPrefix))
        ) {
          fieldDistance = fuzzy.distance;
          fieldPrefix = fuzzy.prefix;
        }
      }
      if (fieldQuality > bestQuality) {
        bestQuality = fieldQuality;
        fields.clear();
        fields.add(field.kind);
      } else if (fieldQuality > 0 && fieldQuality === bestQuality) {
        fields.add(field.kind);
      }
      if (
        bestQuality === 0 &&
        (fieldDistance < bestDistance ||
          (fieldDistance === bestDistance && fieldPrefix > bestPrefix))
      ) {
        bestDistance = fieldDistance;
        bestPrefix = fieldPrefix;
        fields.clear();
        if (Number.isFinite(fieldDistance)) fields.add(field.kind);
      } else if (
        bestQuality === 0 &&
        Number.isFinite(fieldDistance) &&
        fieldDistance === bestDistance &&
        fieldPrefix === bestPrefix
      ) {
        fields.add(field.kind);
      }
    }
    if (bestQuality > 0) {
      return { queryToken: word, quality: qualityName(bestQuality), fields: [...fields] };
    }
    return {
      queryToken: word,
      quality: 'typo',
      fields: [...fields],
      editDistance: Number.isFinite(bestDistance) ? bestDistance : undefined,
    };
  });
}

function emptyResponse(
  prepared: PreparedQuery,
  metrics: JournalSearchQueryMetrics,
  corpusTruncated: boolean,
): JournalSearchResponse {
  return {
    results: [],
    normalizedQuery: prepared.normalized,
    queryTokens: prepared.tokens,
    fuzzyFallback: false,
    matchedCount: 0,
    matchedCountIsExact:
      !metrics.workTruncated && !metrics.queryTruncated && !corpusTruncated,
    resultsTruncated: false,
    corpusTruncated,
    metrics,
  };
}

/** Search a built index. Every returned row satisfies AND across bounded tokens. */
export function searchJournalIndex(
  index: JournalSearchIndex,
  query: string,
  options: { limit?: number } = {},
): JournalSearchResponse {
  const prepared = prepareQuery(query);
  const metrics = initialQueryMetrics(query, prepared);
  const corpusTruncated =
    index.buildMetrics.sourceTruncatedCount > 0 ||
    !index.buildMetrics.literalIndexComplete;
  if (prepared.tokens.length === 0) {
    return emptyResponse(prepared, metrics, corpusTruncated);
  }
  const limit = clampInteger(
    options.limit,
    JOURNAL_SEARCH_LIMITS.defaultResults,
    JOURNAL_SEARCH_LIMITS.maxResults,
  );

  const literal = literalCandidates(prepared, index, metrics);
  const references = structuralReferenceMatches(prepared, index, metrics);
  for (const documentIndex of references) {
    const current = literal.get(documentIndex);
    if (current) current.structuralReference = true;
    else {
      literal.set(documentIndex, {
        structuralReference: true,
        weakestQuality: LITERAL_EXACT,
        qualityTotal: LITERAL_EXACT * prepared.tokens.length,
        exactCount: prepared.tokens.length,
        prefixCount: 0,
      });
    }
  }

  let fuzzyFallback = false;
  let fuzzyTokenMatchesByWord:
    | readonly (ReadonlyMap<number, FuzzyTokenMatch>)[]
    | undefined;
  let ranked: Array<{
    documentIndex: number;
    literal?: LiteralCandidate;
    fuzzy?: FuzzyCandidate;
  }>;
  if (literal.size > 0) {
    ranked = [...literal].map(([documentIndex, candidate]) => ({
      documentIndex,
      literal: candidate,
    }));
    ranked.sort((left, right) => {
      const a = left.literal!;
      const b = right.literal!;
      if (a.structuralReference !== b.structuralReference) {
        return a.structuralReference ? -1 : 1;
      }
      if (b.weakestQuality !== a.weakestQuality) {
        return b.weakestQuality - a.weakestQuality;
      }
      if (b.qualityTotal !== a.qualityTotal) return b.qualityTotal - a.qualityTotal;
      if (b.exactCount !== a.exactCount) return b.exactCount - a.exactCount;
      if (b.prefixCount !== a.prefixCount) return b.prefixCount - a.prefixCount;
      return compareStableDocuments(
        index.documents[left.documentIndex],
        index.documents[right.documentIndex],
      );
    });
  } else {
    fuzzyFallback = true;
    const fuzzy = fuzzyCandidates(prepared, index, metrics);
    fuzzyTokenMatchesByWord = fuzzy.tokenMatchesByWord;
    ranked = [...fuzzy.candidates].map(([documentIndex, candidate]) => ({
      documentIndex,
      fuzzy: candidate,
    }));
    ranked.sort((left, right) => {
      const a = left.fuzzy!;
      const b = right.fuzzy!;
      if (a.distance !== b.distance) return a.distance - b.distance;
      if (b.literalWords !== a.literalWords) return b.literalWords - a.literalWords;
      if (b.prefix !== a.prefix) return b.prefix - a.prefix;
      return compareStableDocuments(
        index.documents[left.documentIndex],
        index.documents[right.documentIndex],
      );
    });
  }

  metrics.candidateCount = ranked.length;
  const selected = ranked.slice(0, limit);
  const results = selected.map(({ documentIndex, literal: literalScore }) => {
    const document = index.documents[documentIndex];
    const structural = literalScore?.structuralReference === true;
    const termMatches = structural
      ? [
          {
            queryToken: prepared.normalized,
            quality: 'reference' as const,
            fields: ['reference' as const],
          },
        ]
      : describeTermMatches(
          document,
          prepared,
          index,
          fuzzyTokenMatchesByWord,
        );
    const matchedFields = FIELD_ORDER.filter((field) =>
      termMatches.some((match) => match.fields.includes(field)),
    );
    const matchKind: JournalSearchMatchKind = structural
      ? 'reference'
      : fuzzyFallback
        ? 'typo'
        : qualityName(literalScore?.weakestQuality ?? LITERAL_SUBSTRING);
    return {
      entry: document.entry,
      sourceId: document.sourceId,
      kind: document.kind,
      matchKind,
      matchedFields,
      termMatches,
    };
  });
  metrics.returnedCount = results.length;
  const matchedCountIsExact =
    !metrics.workTruncated &&
    !metrics.queryTruncated &&
    !corpusTruncated &&
    index.buildMetrics.literalIndexComplete &&
    (!fuzzyFallback || index.buildMetrics.fuzzyIndexComplete);
  return {
    results,
    normalizedQuery: prepared.normalized,
    queryTokens: prepared.tokens,
    fuzzyFallback,
    matchedCount: ranked.length,
    matchedCountIsExact,
    resultsTruncated: ranked.length > results.length,
    corpusTruncated,
    metrics,
  };
}
